import {
  Button,
  TextEditor,
  Input,
  Icon,
  Typography,
  Image,
  Notification
} from '@smartgift/frontend-toolkit';
import React, { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { DigitalWrapperModal, UploadLogoModal } from '@hero/components/modals';
import { configs } from '@hero/configs';
import {
  CONTACT_TYPE,
  MAX_ADDITIONAL_THOUGHTS_LENGTH,
  MAX_SENDER_NAME_LENGTH,
  RECOGNITION_STATUSES
} from '@hero/constants';
import { DetailItem, MessageLibraryModal, RecognitionCustomizeMessageModal } from '@hero/molecules';
import { GiftMessage } from '@hero/organisms';
import {
  useGetRecognition,
  useGetRecognitionRecipientList,
  useUpdateRecognition
} from '@hero/services/order-service';
import { getSafeInnerHtml } from '@hero/utils';

import * as S from './style';

function RecognitionExperienceAccordion({ hideRecipientExperienceButton = true }) {
  const { t } = useTranslation();
  const { recognitionCode } = useParams();

  const { data: recognition, isSuccess: isSuccessRecognition } = useGetRecognition({
    recognitionCode
  });
  const { mutateAsync: updateRecognition, isLoading: isLoadingUpdateRecognition } =
    useUpdateRecognition();

  const [showMessageLibrary, setShowMessageLibrary] = React.useState(false);
  const [experienceSettings, setExperienceSettings] = React.useState();
  const [isUploadLogoModalOpen, setIsUploadLogoModalOpen] = React.useState(false);
  const [isUploadDigitalWrapperModalOpen, setIsUploadDigitalWrapperModalOpen] =
    React.useState(false);
  const [isOpenCustomizeMessageModal, setOpenCustomizeMessageModal] = React.useState();

  const logoUrl = experienceSettings?.branding?.logoUrl;
  const giftWrapperUrl = experienceSettings?.branding?.giftWrapperUrl;

  const isDetailItemEditable = React.useMemo(
    () =>
      recognition?.status?.current === RECOGNITION_STATUSES.live ||
      recognition?.status?.current === RECOGNITION_STATUSES.draft,
    [recognition]
  );

  const { data: customMessagesRecipients } = useGetRecognitionRecipientList({
    code: recognitionCode,
    options: {
      exists: [
        {
          field: 'experienceSettings.customizedGiftMessage',
          exist: true
        }
      ]
    }
  });

  const customMessageLength = React.useMemo(() => {
    return customMessagesRecipients?.totalDocs;
  }, [customMessagesRecipients]);

  useEffect(() => {
    if (recognition?.experienceSettings) {
      setExperienceSettings(recognition?.experienceSettings);
    }
  }, [recognition]);

  return (
    <S.Experience>
      {!hideRecipientExperienceButton && (
        <S.OverviewWrapper>
          <S.ViewRecipientExperienceWrapper>
            <Button color="tertiary" startIcon={<Icon name="preview" />}>
              {t('viewGiftPreview')}
            </Button>
          </S.ViewRecipientExperienceWrapper>
        </S.OverviewWrapper>
      )}
      {recognition?.status?.current === RECOGNITION_STATUSES.live &&
        recognition?.contacts.type === CONTACT_TYPE.list && (
          <Notification
            status="info"
            message={
              <Trans i18nKey="updatingRecognitionExperienceInfo" components={{ b: <b /> }} />
            }
          />
        )}

      <GiftMessage
        setShowMessageLibrary={setShowMessageLibrary}
        setExperienceSettings={setExperienceSettings}
        experienceSettings={experienceSettings}
        associationData={recognition}
        updateAssociation={updateRecognition}
        isDetailItemEditable={isDetailItemEditable}
        isSuccessAssociation={isSuccessRecognition}
        isOpenCustomizeMessageModal={isOpenCustomizeMessageModal}
        setOpenCustomizeMessageModal={setOpenCustomizeMessageModal}
        footerProps={{
          editable: recognition?.status?.current === RECOGNITION_STATUSES.draft,
          customizeActive: recognition?.contacts?.type === CONTACT_TYPE.list,
          tagActive: recognition?.contacts?.type === CONTACT_TYPE.list
        }}
        customMessageLength={customMessageLength}
        isReviewMessageBadgeShown={
          customMessageLength > 0 && recognition?.contacts?.type === CONTACT_TYPE.list
        }
      />

      <DetailItem
        title={`${t('logo')}:`}
        withBorder
        editable={isDetailItemEditable}
        onClickEditButton={() => setIsUploadLogoModalOpen(true)}
        editButton={() => <Icon name="edit" />}
      >
        <S.DigitalWrapperContainer>
          {logoUrl && <Image src={logoUrl} width={80} />}
        </S.DigitalWrapperContainer>

        <UploadLogoModal
          isOpen={isUploadLogoModalOpen}
          setIsOpen={setIsUploadLogoModalOpen}
          uploadAction={({ cdnUrl }) =>
            updateRecognition({
              params: {
                recognitionCode
              },
              body: {
                experienceSettings: {
                  ...experienceSettings,
                  branding: {
                    ...experienceSettings.branding,
                    logoUrl: cdnUrl
                  }
                }
              }
            })
          }
        />
      </DetailItem>

      <DetailItem
        title={`${t('senderName')}:`}
        withBorder
        bodyStyle={{
          display: 'flex',
          alignItems: 'center'
        }}
        editable={isDetailItemEditable}
        closeWithoutSave={() => {
          setExperienceSettings({
            ...experienceSettings,
            messageParams: {
              ...experienceSettings.messageParams,
              senderName: recognition?.experienceSettings?.messageParams?.senderName
            }
          });
        }}
        saveDetail={() => {
          updateRecognition({
            params: { recognitionCode },
            body: {
              experienceSettings
            }
          });
        }}
        editInput={() => (
          <Input
            value={experienceSettings?.messageParams?.senderName}
            inputProps={{
              maxLength: MAX_SENDER_NAME_LENGTH,
              onChange: e => {
                setExperienceSettings({
                  ...experienceSettings,
                  messageParams: {
                    ...experienceSettings.messageParams,
                    senderName: e.target.value
                  }
                });
              }
            }}
            formItem={{
              placeholder: t('senderName'),
              label: t('senderName'),
              name: 'senderName',
              validations: {
                required: {
                  required: true,
                  message: t('inputEmptyWarning', { name: t('senderName') })
                }
              }
            }}
          />
        )}
      >
        <S.OverviewWrapper>
          <Typography variant="body1" color="fg">
            {experienceSettings?.messageParams?.senderName}
          </Typography>
        </S.OverviewWrapper>
      </DetailItem>

      {recognition?.contacts?.type === CONTACT_TYPE.list && (
        <RecognitionCustomizeMessageModal
          code={recognitionCode}
          editable={recognition?.status?.current === RECOGNITION_STATUSES.draft}
          isOpen={isOpenCustomizeMessageModal}
          setIsOpen={setOpenCustomizeMessageModal}
          defaultMessage={recognition?.experienceSettings?.messageParams?.message}
        />
      )}

      {configs?.advanceSettings?.digitalWrapperEnabled && (
        <DetailItem
          title={`${t('digitalWrapper')}:`}
          editable={isDetailItemEditable}
          withBorder
          onClickEditButton={() => setIsUploadDigitalWrapperModalOpen(true)}
          setIsOpen={setIsUploadDigitalWrapperModalOpen}
          editButton={() => <Icon name="edit" />}
        >
          <S.DigitalWrapperContainer>
            {giftWrapperUrl && <Image src={giftWrapperUrl} width={80} />}
          </S.DigitalWrapperContainer>

          <DigitalWrapperModal
            branding={experienceSettings?.branding}
            isOpen={isUploadDigitalWrapperModalOpen}
            setIsOpen={setIsUploadDigitalWrapperModalOpen}
            isUpdating={isLoadingUpdateRecognition}
            onUpdateData={({ branding }) => {
              updateRecognition({
                params: {
                  recognitionCode
                },
                body: {
                  experienceSettings: {
                    ...experienceSettings,
                    branding: {
                      ...experienceSettings.branding,
                      ...branding
                    }
                  }
                }
              });
            }}
          />
        </DetailItem>
      )}

      <DetailItem
        title={`${t('collectionDescription')}:`}
        actionAreaStyle={{
          top: '142px',
          bottom: 'auto'
        }}
        editable={isDetailItemEditable}
        closeWithoutSave={() => {
          setExperienceSettings({
            ...experienceSettings,
            messageParams: {
              ...experienceSettings.messageParams,
              aboutMessage: recognition?.experienceSettings?.messageParams?.aboutMessage
            }
          });
        }}
        saveDetail={() => {
          updateRecognition({
            params: { recognitionCode },
            body: {
              experienceSettings
            }
          });
        }}
        editInput={() =>
          isSuccessRecognition && (
            <TextEditor
              key="additional-thoughts-editor"
              label={t('additionalThoughtsLabel')}
              value={experienceSettings?.messageParams?.aboutMessage}
              showToolbar={false}
              onChange={e => {
                setExperienceSettings({
                  ...experienceSettings,
                  messageParams: {
                    ...experienceSettings.messageParams,
                    aboutMessage: e
                  }
                });
              }}
              limit={MAX_ADDITIONAL_THOUGHTS_LENGTH}
            />
          )
        }
      >
        <S.OverviewWrapper>
          <Typography
            color="fg"
            variant="body1"
            {...getSafeInnerHtml(experienceSettings?.messageParams?.aboutMessage)}
          />
        </S.OverviewWrapper>
      </DetailItem>

      {showMessageLibrary && (
        <MessageLibraryModal
          isOpen={showMessageLibrary}
          onClose={() => setShowMessageLibrary(false)}
        />
      )}
    </S.Experience>
  );
}

export { RecognitionExperienceAccordion };
